<template>
  <div class="ibox">
    <div class="ibox-content">
      <div class="row">
        <div class="col-lg-12">
          <div class="m-b-md">
            <el-dropdown class="pull-right" @command="handleCommand">
              <el-button type="primary" size="mini" icon="el-icon-more" plain
                >More Options</el-button
              >
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="edit-campaign">Edit</el-dropdown-item>
                <el-dropdown-item :divided="true" command="campaign-report"
                  >Campaign Report</el-dropdown-item
                >
                <el-dropdown-item v-if="survey" :divided="true" command="survey-report"
                  >Survey Report</el-dropdown-item
                >
                <el-dropdown-item v-show="bots.length ? true : false" command="bot-report"
                  >Bot Report
                </el-dropdown-item>
                <el-dropdown-item :divided="true" command="download-messages"
                  >Download messages</el-dropdown-item
                >
                <el-dropdown-item command="random-select">Select a random winner</el-dropdown-item>
                <el-dropdown-item command="random-selection">Random selections</el-dropdown-item>
                <can action="view" subject="campaigns-offline">
                  <el-dropdown-item :divided="true" v-if="live" command="toggle-live-status"
                    >Take Offline</el-dropdown-item
                  >
                </can>
                <el-dropdown-item :divided="true" v-if="!live" command="toggle-live-status"
                  >Take Online</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
            <div
              class="favourite active pull-left"
              :class="{ active: isFavourite, inactive: !isFavourite }"
              @click="handleToggleFavourite"
            >
              <i
                class="fa fa-fw"
                :class="{ 'fa-star': isFavourite, 'fa-star-o': !isFavourite }"
              ></i>
            </div>
            <h2>
              <router-link :to="`/campaigns/${id}`">{{ title }}</router-link>
            </h2>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-3">
          <dl class="dl-horizontal">
            <dt>Status:</dt>
            <dd>
              <span class="label label-primary" v-if="live">Active</span>
              <span class="label label-default" v-if="!live">Inactive</span>
            </dd>
          </dl>
        </div>
        <div class="col-lg-5">
          <dl class="dl-horizontal">
            <dt>Type:</dt>
            <dd>
              <span class="label label-success" v-if="campaignType === 'COMPETITION'"
                >Competition</span
              >
              <span class="label label-danger" v-if="campaignType === 'MESSAGE_BOARD'"
                >Message Board</span
              >
              <span class="label label-info" v-if="campaignType === 'POLL'">Poll</span>
              <span class="label label-primary" v-if="campaignType === 'SURVEY'">Survey</span>
              <span class="label label-warning" v-if="campaignType === 'INSTANT_POLL'"
                >Instant Poll</span
              >
            </dd>
          </dl>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-3">
          <dl class="dl-horizontal">
            <!--<div v-if="campaignType === 'COMPETITION'">
              <dt>Advertiser:</dt>
              <dd>{{ advertiser }}</dd>
            </div>-->
            <dt>Total Messages:</dt>
            <dd>{{ Math.round(totalMessages).toLocaleString() }}</dd>
            <dt>Unique Messages:</dt>
            <dd>{{ Math.round(uniqueMessages).toLocaleString() }}</dd>
            <dt>Growth</dt>
            <dd>
              <growth :value="growth" :isPercentage="true" />
            </dd>
            <div v-if="leadCount && leadCount > 0">
              <dt>Lead Total:</dt>
              <dd>{{ leadCount }}</dd>
            </div>
          </dl>
        </div>
        <div class="col-lg-5">
          <dl class="dl-horizontal">
            <dt>Start Date:</dt>
            <dd>{{ startDate.format('ddd Do MMMM YYYY') }}</dd>
            <dt>End Date:</dt>
            <dd v-if="perpetual"><span class="label label-primary">Perpetual Campaign</span></dd>
            <dd v-else>{{ endDate.format('ddd Do MMMM YYYY') }}</dd>
            <dt>Created By:</dt>
            <dd>{{ createdBy ? createdBy.displayName : '' }}</dd>

            <dt>Progress:</dt>
            <dd>
              <div class="progress m-b-sm">
                <div :style="{ width: `${adjustedProgress}%` }" class="progress-bar"></div>
              </div>
              <small v-if="adjustedProgress < 100">
                This campaign is currently
                <strong>{{ adjustedProgress }}%</strong>
                completed and will end
                <strong>{{ endDate.fromNow() }}</strong
                >.
              </small>
              <small v-if="adjustedProgress === 100">
                This campaign has
                <strong>completed {{ endDate.fromNow() }}</strong
                >.
              </small>
            </dd>

            <dt v-if="topEngagers.length">Top Engagers:</dt>
            <dd class="project-people">
              <engager
                v-for="(profile, index) in topEngagers"
                :key="index"
                :profile="profile"
                @click.native.prevent="showProfileDialog(profile, id)"
              ></engager>
            </dd>
          </dl>
        </div>
        <div class="col-lg-3">
          <small>Interactions this month</small>
          <h2 class="no-margins">{{ Math.round(totalThisMonth).toLocaleString() }}</h2>
          <trend :points="totalPerDayThisMonth" :height="50" />
        </div>
      </div>
      <div class="row">
        <hr />
      </div>
      <div class="row">
        <div
          :class="{ 'col-lg-8': replyMessage.length > 0, 'col-lg-12': replyMessage.length === 0 }"
        >
          <h3>Channels</h3>
          <table class="table table-striped" v-if="channelLinks.length">
            <tbody>
              <can action="view" subject="unlink-channels">
                <channel-to-campaign-link-item
                  v-for="item in channelLinks"
                  :key="item.id"
                  :keyword="item.keyword"
                  :handle="item.channel.handle"
                  :channelType="item.channel.channelTypeTitle"
                  @onUnlink="handleUnlink(item.id)"
                />
              </can>
            </tbody>
          </table>
          <div v-if="!channelLinks.length" class="no-channels">
            <span>You currently have no channels linked on this campaign.</span>
          </div>
        </div>
        <div class="col-lg-4" v-if="replyMessage.length > 0">
          <h5>Reply Messages</h5>
          <p class="text-muted">
            <em>{{ replyMessage }}</em>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import jdenticon from 'jdenticon';
import Trend from '@/components/charts/Trend';
import Can from '@/components/permissions/Can';
import Growth from '@/components/common/Growth';
import Engager from '@/components/campaign/CampaignEngager';
import ChannelToCampaignLinkItem from '@/components/campaign/ChannelToCampaignLinkItem';

export default {
  name: 'CampaignListItem',
  components: {
    Can,
    Trend,
    Growth,
    Engager,
    ChannelToCampaignLinkItem,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    live: {
      type: Boolean,
      default: false,
    },
    perpetual: {
      type: Boolean,
      default: false,
    },
    campaignType: {
      type: String,
      default: 'MESSAGE_BOARD',
    },
    startDate: {
      type: Object,
      required: true,
    },
    endDate: {
      type: Object,
      required: true,
    },
    createdBy: {
      type: Object,
      default: () => ({ displayName: '' }),
    },
    advertiser: {
      type: String,
      default: '',
    },
    progress: {
      type: Number,
      default: 0,
    },
    totalMessages: {
      type: Number,
      default: 0,
    },
    uniqueMessages: {
      type: Number,
      default: 0,
    },
    totalThisMonth: {
      type: Number,
      default: 0,
    },
    totalPerDayThisMonth: {
      type: Array,
      default: () => [],
    },
    channelLinks: {
      type: Array,
      default: () => [],
    },
    replyMessage: {
      type: String,
      default: '',
    },
    growth: {
      type: Number,
      default: 0,
    },
    isFavourite: {
      type: Boolean,
      default: false,
    },
    topEngagers: {
      type: Array,
      default: () => [],
    },
    survey: {
      type: Object,
      default: () => ({}),
    },
    bots: {
      type: Array,
      default: () => [],
    },
    leadCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    adjustedProgress() {
      return this.progress >= 100 ? 100 : this.progress;
    },
  },
  methods: {
    identicon(originator) {
      return jdenticon.toSvg(originator, 128);
    },

    handleUnlink(linkId) {
      this.$emit('onUnlink', linkId);
    },

    handleToggleFavourite() {
      this.$emit('onFavourite', !this.isFavourite);
    },

    handleCommand(command) {
      const commands = {
        'edit-campaign': this.handleCampaignEdit,
        'campaign-report': this.handleCampaignReport,
        'random-select': this.handleRandomSelect,
        'random-selection': this.handleRandomSelecttion,
        'download-messages': this.handleDownloadMessages,
        'survey-report': this.handleSurveyReport,
        'bot-report': this.handleDownloadBotReport,
        'toggle-live-status': this.handleToggleCampaignStatus,
      };
      return commands[command]();
    },

    handleRandomSelect() {
      this.$emit('onRandomSelect');
    },
    handleRandomSelecttion() {
      this.$emit('onRandomSelections');
    },

    handleDownloadMessages() {
      this.$emit('onDownloadMessages', this.channelLinks);
    },

    handleSurveyReport() {
      this.$emit('onExportSurveyData', this.survey);
    },

    handleDownloadBotReport() {
      this.$emit('onDownloadBotReport', this.channelLinks);
    },

    handleCampaignReport() {
      this.$router.push({ path: `/campaigns/${this.id}` });
    },

    handleCampaignEdit() {
      this.$emit('onEditCampaign');
    },

    handleToggleCampaignStatus() {
      this.$emit('onToggleLiveStatus', !this.live);
    },

    showProfileDialog(profile, campaignId = 0) {
      this.$emit('onSelectProfile', { profile, campaignId });
    },
  },
};
</script>

<style lang="scss" scoped>
.no-channels {
  text-align: center;
  font-size: 1.2em;
  color: #ccc;
  text-shadow: 1px 1px 1px #efefef;
}
.favourite {
  font-size: 19px;
  margin-right: 8px;
  cursor: pointer;

  &.inactive {
    color: #ccc;
  }

  &.active {
    color: #fce205;
  }
}

dt,
dd {
  padding: 3px 0;
}
</style>



// WEBPACK FOOTER //
// CampaignListItem.vue?7210199b